import { useEffect } from 'react';
import { Checkbox, DatePicker, Form, Input, InputNumber, Modal, Select, message } from 'antd';
import dayjs from 'dayjs';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { updateClientStatus } from '@/services/teamAssignments.service';
import { CLIENT_FLAGS, CLIENT_STATUSES } from './model';

/**
 * Whether HLB works for the entity, plus its per-client facts. Status and
 * flags go to every company of the entity; the short name, bank and the note
 * to the main one; payroll headcount to each company of the group on its own
 * (a project of PCCC has its own people).
 */
export default function ClientStatusDialog({ open, companies, primaryId, hasAssignments, onClose, onDone }) {
  const { t } = useTranslation(['teamAssignments', 'teamContacts']);
  const [form] = Form.useForm();
  const status = Form.useWatch('status', form);
  const primary = companies.find((c) => c._id === primaryId) || companies[0];
  const perCompany = companies.length > 1;
  // What a company of this group may be set to in the contacts table: only a
  // branch can go with its main company, only a main company with branches can hide.
  const displayOptionsOf = (c) =>
    [
      'OWN_ROW',
      c.mainCompany && companies.some((x) => x._id === c.mainCompany) ? 'WITH_MAIN' : null,
      companies.some((x) => x.mainCompany === c._id) ? 'HIDE_MAIN' : null,
    ]
      .filter(Boolean)
      .map((value) => ({ value, label: t(`display.${value}`, { ns: 'teamContacts' }) }));
  const hasDisplayChoice = companies.some((c) => displayOptionsOf(c).length > 1);

  useEffect(() => {
    if (!open || !primary) return;
    const h = primary.hlbClient;
    form.setFieldsValue({
      status: h.status,
      endedAt: h.endedAt ? dayjs(h.endedAt) : null,
      flags: [...new Set(companies.flatMap((c) => c.hlbClient.flags || []))],
      shortName: h.shortName,
      note: h.note,
      paymentsBank: h.paymentsBank,
      paymentsMode: h.paymentsMode,
      payrollHeadcount: h.payrollHeadcount,
      headcounts: Object.fromEntries(companies.map((c) => [c._id, c.hlbClient.payrollHeadcount])),
      payrollHeadcountNote: h.payrollHeadcountNote,
      displays: Object.fromEntries(companies.map((c) => [c._id, c.hlbClient.contactsDisplay || 'OWN_ROW'])),
      endAssignments: false,
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: async (v) => {
      const common = {
        status: v.status,
        flags: v.flags || [],
        endedAt: v.endedAt ? v.endedAt.format('YYYY-MM-DD') : null,
        endAssignments: !!v.endAssignments,
      };
      for (const c of companies) {
        const extra =
          c._id === primary._id
            ? {
                shortName: v.shortName || '',
                note: v.note || '',
                paymentsBank: v.paymentsBank || '',
                paymentsMode: v.paymentsMode || '',
                payrollHeadcount: perCompany ? v.headcounts?.[c._id] ?? null : v.payrollHeadcount ?? null,
                payrollHeadcountNote: v.payrollHeadcountNote || '',
              }
            : perCompany
              ? { payrollHeadcount: v.headcounts?.[c._id] ?? null }
              : {};
        const display = v.displays?.[c._id] ? { contactsDisplay: v.displays[c._id] } : {};
        await updateClientStatus(c._id, { ...common, ...extra, ...display });
      }
    },
    onSuccess: () => {
      message.success(t('messages.saved'));
      onDone?.();
      onClose();
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  const served = status === 'ACTIVE' || status === 'OWN';

  return (
    <Modal
      open={open}
      title={t('clientStatus.title')}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t('actions.save')}
      cancelText={t('actions.cancel')}
      confirmLoading={save.isPending}
      okButtonProps={{ shape: 'round' }}
      cancelButtonProps={{ shape: 'round' }}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Form.Item name="status" label={t('clientStatus.status')} rules={[{ required: true }]}>
          <Select options={CLIENT_STATUSES.map((s) => ({ value: s, label: t(`clientStatuses.${s}`) }))} />
        </Form.Item>
        {status === 'FORMER' && (
          <Form.Item name="endedAt" label={t('clientStatus.endedAt')}>
            <DatePicker format="DD.MM.YYYY" style={{ width: 200 }} />
          </Form.Item>
        )}
        {!served && hasAssignments && (
          <Form.Item name="endAssignments" valuePropName="checked">
            <Checkbox>{t('clientStatus.endAssignments')}</Checkbox>
          </Form.Item>
        )}
        <Form.Item name="shortName" label={t('clientStatus.shortName')} extra={t('clientStatus.shortNameHint')}>
          <Input maxLength={120} placeholder={primary?.fullName || primary?.name} />
        </Form.Item>
        <Form.Item name="flags" label={t('clientStatus.flags')}>
          <Checkbox.Group options={CLIENT_FLAGS.map((f) => ({ value: f, label: t(`flags.${f}`) }))} />
        </Form.Item>
        <Form.Item name="note" label={t('clientStatus.note')}>
          <Input.TextArea rows={2} maxLength={1000} />
        </Form.Item>
        <Form.Item name="paymentsBank" label={t('clientStatus.paymentsBank')}>
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="paymentsMode" label={t('clientStatus.paymentsMode')}>
          <Input maxLength={300} placeholder={t('clientStatus.paymentsModePlaceholder')} />
        </Form.Item>
        {perCompany ? (
          <Form.Item label={t('clientStatus.headcountPerCompany')}>
            <div className="grid gap-1.5">
              {companies.map((c) => (
                <div key={c._id} className="grid grid-cols-[minmax(0,1fr)_120px] items-center gap-2 border-b border-zinc-100 pb-1.5 last:border-b-0">
                  <span className="truncate text-sm" title={c.fullName || c.name}>
                    {c.name}
                  </span>
                  <Form.Item name={['headcounts', c._id]} noStyle>
                    <InputNumber min={0} style={{ width: 120 }} />
                  </Form.Item>
                </div>
              ))}
            </div>
          </Form.Item>
        ) : (
          <Form.Item name="payrollHeadcount" label={t('clientStatus.payrollHeadcount')}>
            <InputNumber min={0} style={{ width: 160 }} />
          </Form.Item>
        )}
        <Form.Item name="payrollHeadcountNote" label={t('clientStatus.payrollHeadcountNote')}>
          <Input maxLength={300} />
        </Form.Item>
        {hasDisplayChoice && (
          <Form.Item label={t('display.title', { ns: 'teamContacts' })} extra={t('display.hint', { ns: 'teamContacts' })}>
            <div className="grid gap-1.5">
              {companies.map((c) => (
                <div key={c._id} className="grid grid-cols-[minmax(0,1fr)_180px] items-center gap-2 border-b border-zinc-100 pb-1.5 last:border-b-0">
                  <span className="truncate text-sm" title={c.fullName || c.name}>
                    {c.name}
                  </span>
                  <Form.Item name={['displays', c._id]} noStyle>
                    <Select options={displayOptionsOf(c)} style={{ width: 180 }} />
                  </Form.Item>
                </div>
              ))}
            </div>
          </Form.Item>
        )}
        {companies.length > 1 && <p style={{ opacity: 0.7, margin: 0 }}>{t('clientStatus.groupHint', { count: companies.length })}</p>}
      </Form>
    </Modal>
  );
}
